import React, { useState, useMemo } from "react";
import { useAuthStore } from "../../stores/authStore";
import { toast } from "react-toastify";
import api, { usersApi } from "../../services/api";
import { Edit3, Save, Ban, KeyRound, LockKeyhole, ShieldCheck, ShieldAlert, CheckCircle, AlertTriangle } from "lucide-react";

// Faculty and Major data structure - HCMUT Official Data
const FACULTY_MAJORS_MAP: Record<string, string[]> = {
  "KHOA CƠ KHÍ": [
    "Bảo dưỡng Công nghiệp",
    "Kỹ thuật Cơ khí",
    "Công nghệ Dệt, May",
    "Kỹ thuật Cơ điện tử",
    "Kỹ thuật Dệt",
    "Kỹ thuật Hệ thống Công nghiệp",
    "Logistics và Quản lý Chuỗi cung ứng",
    "Kỹ thuật Nhiệt"
  ],
  "KHOA KỸ THUẬT ĐỊA CHẤT VÀ DẦU KHÍ": [
    "Kỹ thuật Địa chất",
    "Kỹ thuật Dầu khí",
    "Địa Kỹ thuật Xây dựng"
  ],
  "KHOA ĐIỆN - ĐIỆN TỬ": [
    "Kỹ thuật Điều khiển và Tự động hóa",
    "Kỹ thuật Điện tử - Viễn thông",
    "Kỹ thuật Điện",
    "Thiết kế vi mạch"
  ],
  "KHOA KỸ THUẬT GIAO THÔNG": [
    "Kỹ thuật Hàng không",
    "Kỹ thuật Ô tô",
    "Kỹ thuật Tàu thủy",
    "Song ngành: Kỹ thuật Tàu thủy - Hàng không"
  ],
  "KHOA KỸ THUẬT HÓA HỌC": [
    "Công nghệ Sinh học",
    "Kỹ thuật Hóa học",
    "Công nghệ Thực phẩm"
  ],
  "KHOA MÔI TRƯỜNG VÀ TÀI NGUYÊN": [
    "Kỹ thuật Môi trường",
    "Quản lý Tài nguyên và Môi trường",
    "Kinh tế tài nguyên thiên nhiên"
  ],
  "KHOA KHOA HỌC VÀ KỸ THUẬT MÁY TÍNH": [
    "Khoa học Máy tính",
    "Kỹ thuật máy tính"
  ],
  "KHOA QUẢN LÝ CÔNG NGHIỆP": [
    "Quản lý công nghiệp",
    "Quản trị Kinh doanh"
  ],
  "KHOA KHOA HỌC ỨNG DỤNG": [
    "Cơ Kỹ thuật",
    "Vật lý Kỹ thuật",
    "Khoa học dữ liệu"
  ],
  "KHOA KỸ THUẬT XÂY DỰNG": [
    "Kỹ thuật Công trình biển",
    "Kỹ thuật Cơ sở hạ tầng",
    "Công nghệ Kỹ thuật Vật liệu Xây dựng",
    "Kỹ thuật Xây dựng",
    "Kiến trúc",
    "Kỹ thuật Trắc địa - Bản đồ",
    "Kỹ thuật Xây dựng Công trình Thủy",
    "Kỹ thuật Xây dựng Công trình Giao thông",
    "Kinh tế Xây dựng"
  ],
  "VIỆT PHÁP": [
    "Kỹ thuật Cơ điện tử",
    "Hàng không",
    "Vật liệu Polymer và Composite",
    "Vật liệu và Năng lượng",
    "Viễn thông",
    "Hệ thống năng lượng điện",
    "Kỹ thuật và quản lý nước đô thị",
    "Xây dựng dân dụng - Công nghiệp và Hiệu quả"
  ],
  "CHƯƠNG TRÌNH TIÊN TIẾN": [
    "Vi mạch - Hệ thống phần cứng",
    "Hệ thống Năng lượng",
    "Kỹ thuật Điều khiển và Tự động hoá",
    "Hệ thống Viễn thông"
  ]
};

const facultyOptions = Object.keys(FACULTY_MAJORS_MAP).sort();

const TRAINING_PROGRAMS = [
  "Đại học chính quy",
  "Đại học chất lượng cao",
  "Đại học tiên tiến",
  "Đại học định hướng ứng dụng",
  "Thạc sĩ",
  "Tiến sĩ",
];

const Profile: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [hasPendingTutorApplication, setHasPendingTutorApplication] = useState(false);

  const [formData, setFormData] = useState({
    full_name: user?.full_name || "",
    program: user?.program || "",
    faculty: user?.faculty || "",
    major: user?.major || "",
    phone: user?.phone || "",
    bio: user?.bio || "",
  });

  // Fetch fresh profile data on mount
  React.useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response: any = await usersApi.getProfile();
        setUser(response.data);
      } catch (error) {
        console.error("Failed to fetch profile:", error);
      }
    };
    fetchProfile();
  }, [setUser]);

  // Update formData when user data changes
  React.useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || "",
        program: user.program || "",
        faculty: user.faculty || "",
        major: user.major || "",
        phone: user.phone || "",
        bio: user.bio || "",
      });
    }
  }, [user]);

  // Check if user has pending tutor application
  React.useEffect(() => {
    // Temporarily disabled - will show pending status after successful registration
    // const checkTutorStatus = async () => {
    //   if (user?.role === "student" && user?.user_id) {
    //     try {
    //       const response = await api.get("/tutors/me");
    //       if (response.data) {
    //         setHasPendingTutorApplication(true);
    //       }
    //     } catch (error) {
    //       setHasPendingTutorApplication(false);
    //     }
    //   }
    // };
    // checkTutorStatus();
  }, [user?.user_id, user?.role]);

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const availableMajors = useMemo(() => {
    if (!formData.faculty || !FACULTY_MAJORS_MAP[formData.faculty]) {
      return [];
    }
    return FACULTY_MAJORS_MAP[formData.faculty];
  }, [formData.faculty]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await usersApi.updateProfile(formData);
      // Fetch fresh profile to get all fields including student data
      const profileResponse: any = await usersApi.getProfile();
      setUser(profileResponse.data);
      toast.success("Cập nhật thông tin thành công!");
      setIsEditing(false);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Cập nhật thất bại!");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Mật khẩu mới không khớp!");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("Mật khẩu phải có ít nhất 6 ký tự!");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });
      toast.success("Đổi mật khẩu thành công!");
      setIsChangingPassword(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Đổi mật khẩu thất bại!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">Thông tin cá nhân</h1>
        <p className="text-purple-100">Quản lý thông tin tài khoản của bạn</p>
      </div>

      {/* Profile Info */}
      <div className="card">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white text-3xl font-bold">
              {user?.full_name?.charAt(0) || "U"}
            </div>
            <div className="ml-4">
              <h2 className="text-2xl font-bold text-gray-900">
                {user?.full_name}
              </h2>
              <p className="text-gray-600">{user?.email}</p>
              <span className="mt-2 inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {user?.role?.includes("student")
                  ? "Sinh viên"
                  : user?.role?.includes("tutor")
                    ? "Gia sư"
                    : "Admin"}
              </span>
            </div>
          </div>

          <button
            onClick={() => setIsEditing(!isEditing)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            {isEditing ? (
              <>
                <Ban className="h-4 w-4" />
                Hủy
              </>
            ) : (
              <>
                <Edit3 className="h-4 w-4" />
                Chỉnh sửa
              </>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="profile-name" className="block text-sm font-medium text-gray-700 mb-2">
              Họ và tên
            </label>
            {isEditing ? (
              <input
                id="profile-name"
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900 font-medium">{user?.full_name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <p className="text-gray-900 font-medium">{user?.email}</p>
            <p className="text-xs text-gray-500 mt-1">
              Email không thể thay đổi
            </p>
          </div>

          {/* Only show program field for students */}
          {user?.role?.includes('student') && (
            <div>
              <label
                htmlFor="profile-program"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Chương trình đào tạo
              </label>
              {isEditing ? (
                <select
                  id="profile-program"
                  name="program"
                  value={formData.program}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="" disabled>
                    Chọn chương trình
                  </option>
                  {TRAINING_PROGRAMS.map((program) => (
                    <option key={program} value={program}>
                      {program}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-gray-900 font-medium">
                  {formData.program || "-"}
                </p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="profile-faculty" className="block text-sm font-medium text-gray-700 mb-2">
              Khoa
            </label>
            {isEditing ? (
              <select
                id="profile-faculty"
                name="faculty"
                value={formData.faculty}
                onChange={(e) => {
                  const selected = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    faculty: selected,
                    major: "",
                  }));
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="" disabled>
                  Chọn khoa
                </option>
                {facultyOptions.map((faculty) => (
                  <option key={faculty} value={faculty}>
                    {faculty}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-gray-900 font-medium">
                {formData.faculty || "-"}
              </p>
            )}
          </div>

          {/* Only show major field for students */}
          {user?.role?.includes('student') && (
            <div>
              <label htmlFor="profile-major" className="block text-sm font-medium text-gray-700 mb-2">
                Ngành
              </label>
              {isEditing ? (
                <select
                  id="profile-major"
                  name="major"
                  value={formData.major}
                  onChange={handleInputChange}
                  disabled={!formData.faculty}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value="" disabled>
                    {formData.faculty
                      ? "Chọn ngành"
                      : "Chọn khoa trước để xem ngành"}
                  </option>
                  {availableMajors.map((major: string) => (
                    <option key={major} value={major}>
                      {major}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-gray-900 font-medium">{formData.major || "-"}</p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="profile-phone" className="block text-sm font-medium text-gray-700 mb-2">
              Số điện thoại
            </label>
            {isEditing ? (
              <input
                id="profile-phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900 font-medium">{user?.phone || "-"}</p>
            )}
          </div>

          <div>
            <label htmlFor="profile-role" className="block text-sm font-medium text-gray-700 mb-2">
              Vai trò
            </label>
            <div className="flex items-center gap-2">
              <p className="text-gray-900 font-medium capitalize">{user?.role}</p>
              {/* hasPendingTutorApplication && user?.role === "student" && (
                <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full border border-yellow-300">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  Đơn đăng ký Tutor: Đang chờ duyệt
                </span>
              ) */}
            </div>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="profile-bio" className="block text-sm font-medium text-gray-700 mb-2">
              Giới thiệu bản thân
            </label>
            {isEditing ? (
              <textarea
                id="profile-bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                rows={4}
                placeholder="Viết vài dòng giới thiệu về bản thân, sở thích, kỹ năng..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            ) : (
              <p className="text-gray-900 whitespace-pre-wrap">{user?.bio || "Chưa có thông tin giới thiệu"}</p>
            )}
          </div>
        </div>

        {isEditing && (
          <div className="mt-6 flex gap-3">
            <button
              onClick={handleSaveProfile}
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {loading ? (
                "Đang lưu..."
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Lưu thay đổi
                </>
              )}
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="inline-flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              <Ban className="h-4 w-4" />
              Hủy
            </button>
          </div>
        )}
      </div>

      {/* Change Password */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Đổi mật khẩu</h2>
            <p className="text-sm text-gray-600 mt-1">
              Cập nhật mật khẩu để bảo mật tài khoản
            </p>
          </div>
          <button
            onClick={() => setIsChangingPassword(!isChangingPassword)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
          >
            {isChangingPassword ? (
              <>
                <Ban className="h-4 w-4" />
                Hủy
              </>
            ) : (
              <>
                <KeyRound className="h-4 w-4" />
                Đổi mật khẩu
              </>
            )}
          </button>
        </div>

        {isChangingPassword && (
          <div className="space-y-4">
            <div>
              <label htmlFor="password-current" className="block text-sm font-medium text-gray-700 mb-2">
                Mật khẩu hiện tại
              </label>
              <input
                id="password-current"
                type="password"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="password-new" className="block text-sm font-medium text-gray-700 mb-2">
                Mật khẩu mới
              </label>
              <input
                id="password-new"
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                placeholder="Ít nhất 6 ký tự"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="password-confirm" className="block text-sm font-medium text-gray-700 mb-2">
                Xác nhận mật khẩu mới
              </label>
              <input
                id="password-confirm"
                type="password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                placeholder="Nhập lại mật khẩu mới"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleChangePassword}
                disabled={loading}
                className="inline-flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  "Đang xử lý..."
                ) : (
                  <>
                    <LockKeyhole className="h-4 w-4" />
                    Cập nhật mật khẩu
                  </>
                )}
              </button>
              <button
                onClick={() => setIsChangingPassword(false)}
                className="inline-flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                <Ban className="h-4 w-4" />
                Hủy
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Account Info */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          Thông tin tài khoản
        </h2>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b">
            <span className="text-gray-600">Trạng thái tài khoản</span>
            <span
              className={`font-medium ${user?.is_active ? "text-green-600" : "text-red-600"
                }`}
            >
              {user?.is_active ? (
                <span className="inline-flex items-center gap-1">
                  <ShieldCheck className="h-4 w-4" />
                  Đang hoạt động
                </span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <ShieldAlert className="h-4 w-4" />
                  Bị khóa
                </span>
              )}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span className="text-gray-600">Email đã xác thực</span>
            <span
              className={`font-medium flex items-center gap-1 ${user?.is_verified ? "text-green-600" : "text-yellow-600"
                }`}
            >
              {user?.is_verified ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Đã xác thực
                </>
              ) : (
                <>
                  <AlertTriangle className="w-4 h-4" />
                  Chưa xác thực
                </>
              )}
            </span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Ngày tạo tài khoản</span>
            <span className="font-medium text-gray-900">
              {user?.created_at
                ? new Date(user.created_at).toLocaleDateString("vi-VN")
                : "-"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
